import { paramCase } from 'change-case'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames } from '@opallabs/kuiper'
import { doNothing } from '../../helpers/functions/doNothing'
import { useValidModules } from '../../helpers/hooks'
import type {
  GetContentByIDV2Return,
  LinkWithData,
  Module,
  ModuleDefinition,
  OnAddSectionProps,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
} from '../../helpers/types'
import { TwitterGif } from '../TwitterGif'
import { TwitterPoll } from '../TwitterPoll'
import { TwitterRetweet } from '../TwitterRetweet'
import { TwitterTweet } from '../TwitterTweet'
import { TwitterVideo } from '../TwitterVideo'
import { Stream } from './Stream'
import { baseStyles } from './styles'

interface Props {
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number, index: number) => void
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  display: string
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  isAccountPremium?: boolean
  isPresenting: boolean
  linksWithData: LinkWithData[]
  modules: Module[]
  onAddSection?: (args: OnAddSectionProps) => void
  onDeleteSection: (indexToRemove: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  postType: string
  readOnly: boolean
  systemScheduledAt: string
  timezone: string
  updateCoverAsset: (
    assetData: FileList,
    assetId: number,
    index: number
  ) => void
  updateLinkData: OnLinkDataUpdate
  updateSectionPostOption: (index: number, value: string, name: string) => void
  uploadAssets: (assets: FileList, args: OnShowAssetPickerProps) => void
}

const availableModules: ModuleDefinition[] = [
  {
    component: TwitterTweet,
    componentName: 'twitter_thread_image',
    title: 'Image',
  },
  {
    component: TwitterGif,
    componentName: 'twitter_thread_gif',
    title: 'GIF',
  },
  {
    component: TwitterVideo,
    componentName: 'twitter_thread_video',
    title: 'Video',
  },
  {
    component: TwitterTweet,
    componentName: 'twitter_thread_link_preview',
    title: 'Link Preview',
  },
  {
    component: TwitterPoll,
    componentName: 'twitter_thread_poll',
    title: 'Poll',
  },
  {
    component: TwitterRetweet,
    componentName: 'twitter_thread_retweet',
    title: 'Repost',
  },
]

export const TwitterThread: React.FC<Props> = ({
  onExpandAsset = () => null,
  onRemoveAsset = () => null,
  accountImage,
  accountName,
  accountUsername,
  display,
  getContentByIdV2,
  isAccountPremium,
  isPresenting = false,
  linksWithData,
  modules = [],
  onAddSection,
  onDeleteSection = () => null,
  onShowAssetPicker = () => null,
  postType,
  readOnly = false,
  systemScheduledAt,
  timezone,
  updateCoverAsset = () => null,
  updateLinkData = () => null,
  updateSectionPostOption = () => null,
  uploadAssets = () => null,
}) => {
  const validModules = useValidModules({
    modules,
    onAddSection: onAddSection ? onAddSection : doNothing,
    minModulesLength: 0,
    componentNames: [
      'twitter_thread_image',
      'twitter_thread_gif',
      'twitter_thread_video',
      'twitter_thread_link_preview',
      'twitter_thread_poll',
      'twitter_thread_retweet',
    ],
  })

  return (
    <div
      css={baseStyles(display)}
      className={kuiperClassNames(
        'twitter twitter-thread',
        `twitter-${paramCase(display)}`
      )}
    >
      <Stream
        accountImage={accountImage}
        accountName={accountName}
        accountUsername={accountUsername}
        availableModules={availableModules}
        display={display}
        getContentByIdV2={getContentByIdV2}
        isAccountPremium={isAccountPremium}
        isPresenting={isPresenting}
        linksWithData={linksWithData}
        modules={validModules}
        onAddSection={onAddSection ? onAddSection : doNothing}
        onDeleteSection={onDeleteSection}
        onExpandAsset={onExpandAsset}
        onRemoveAsset={onRemoveAsset}
        onShowAssetPicker={onShowAssetPicker}
        postType={postType}
        readOnly={readOnly}
        systemScheduledAt={systemScheduledAt}
        timezone={timezone}
        updateCoverAsset={updateCoverAsset}
        updateLinkData={updateLinkData}
        updateSectionPostOption={updateSectionPostOption}
        uploadAssets={uploadAssets}
      />
    </div>
  )
}
